import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Controller,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { Button } from "../../../../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Switch } from "../../../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { DatePicker } from "../../../runQuote/components/DatePicker";
import { FieldError } from "../../../runQuote/components/FieldError";
import { Step4Schema } from "../../utils/schema";

const MARITAL_STATUS = [
  { value: "Single", label: "Single" },
  { value: "Married", label: "Married" },
  { value: "Separated", label: "Separated" },
  { value: "Widowed", label: "Widowed" },
  { value: "Divorced", label: "Divorced" },
];

const EDUCATION_LEVELS = [
  { value: "Graduate", label: "Graduate" },
  { value: "Bachelors", label: "Bachelors" },
  { value: "Associates", label: "Associates" },
  { value: "Currently In College", label: "Currently In College" },
  { value: "Vocational Degree", label: "Vocational Degree" },
  { value: "HS Diploma", label: "HS Diploma" },
  { value: "No HS Diploma", label: "No HS Diploma" },
];

const LICENSE_TYPES = [
  { value: "Licensed", label: "Licensed" },
  { value: "Learners Permit", label: "Learners Permit" },
  { value: "Not Licensed", label: "Not Licensed" },
];

const LICENSE_DURATION = [
  { value: "Less than 1 year", label: "Less than 1 year" },
  { value: "1-2 years", label: "1-2 years" },
  { value: "2-3 years", label: "2-3 years" },
  { value: "3+ years", label: "3+ years" },
];

const EMPLOYMENT_TYPES = [
  { value: "Full time", label: "Full time" },
  { value: "Part Time", label: "Part Time" },
  { value: "Unemployed", label: "Unemployed" },
  { value: "Student", label: "Student" },
];

const DRIVER_STATUS = [
  { value: "Rated", label: "Rated" },
  { value: "Excluded", label: "Excluded" },
];

const GENDER_OPTIONS = [
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
];

const emptyDriver = {
  firstName: "",
  lastName: "",
  maritalStatus: "",
  dateOfBirth: "",
  gender: "",
  educationLevel: "",
  licenseType: "",
  sr22: false,
  licensedDuration: "",
  driverLicense: "",
  employmentType: "",
  employmentLine: "",
  autoDriverStatus: "",
  distantStudentDiscount: false,
  goodStudentDiscount: false,
};

const driverSchema = Step4Schema.shape.drivers.element;

function DriverDialog({ open, onOpenChange, onSave, defaultValues }) {
  const methods = useForm({
    defaultValues,
    resolver: zodResolver(driverSchema),
    mode: "onChange",
  });
  const { register, control, handleSubmit, reset, watch, trigger, formState } = methods;
  const employmentType = watch("employmentType");
  const [openDob, setOpenDob] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    reset(defaultValues);
    trigger();
  }, [defaultValues, reset, open, trigger]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Driver Details</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((values) => {
            onSave(values);
          })}
          className="space-y-6"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="driverFirstName">First Name</Label>
              <Input id="driverFirstName" placeholder="Enter..." {...register("firstName")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="driverLastName">Last Name</Label>
              <Input id="driverLastName" placeholder="Enter..." {...register("lastName")} />
            </div>
            <div className="space-y-2">
              <Label>Marital Status</Label>
              <Controller
                control={control}
                name="maritalStatus"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Status</SelectLabel>
                        {MARITAL_STATUS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <DatePicker
              control={control}
              open={openDob}
              setOpen={setOpenDob}
              label="Date of Birth"
              name="dateOfBirth"
              step="driver"
            />
            <div className="space-y-2">
              <Label>Gender</Label>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Gender</SelectLabel>
                        {GENDER_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>Highest Level of Education</Label>
              <Controller
                control={control}
                name="educationLevel"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Education</SelectLabel>
                        {EDUCATION_LEVELS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>License Type</Label>
              <Controller
                control={control}
                name="licenseType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>License</SelectLabel>
                        {LICENSE_TYPES.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>How long has the driver been licensed?</Label>
              <Controller
                control={control}
                name="licensedDuration"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Duration</SelectLabel>
                        {LICENSE_DURATION.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label>Employment Type</Label>
              <Controller
                control={control}
                name="employmentType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Employment</SelectLabel>
                        {EMPLOYMENT_TYPES.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            {(employmentType === "Full time" || employmentType === "Part Time") && (
              <div className="space-y-2">
                <Label htmlFor="employmentLine">What line of work is the insured in?</Label>
                <Input id="employmentLine" placeholder="Enter..." {...register("employmentLine")} />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="driverLicense">Driver's License</Label>
              <Input id="driverLicense" placeholder="Enter..." {...register("driverLicense")} />
            </div>
            <div className="space-y-2">
              <Label>Auto Driver Status</Label>
              <Controller
                control={control}
                name="autoDriverStatus"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Status</SelectLabel>
                        {DRIVER_STATUS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="sr22"
                render={({ field }) => (
                  <Switch id="sr22" checked={!!field.value} onCheckedChange={field.onChange} />
                )}
              />
              <Label htmlFor="sr22">Does Driver Require an SR22?</Label>
            </div>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="distantStudentDiscount"
                render={({ field }) => (
                  <Switch
                    id="distantStudentDiscount"
                    checked={!!field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label htmlFor="distantStudentDiscount">Distant Student Discount?</Label>
            </div>
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="goodStudentDiscount"
                render={({ field }) => (
                  <Switch
                    id="goodStudentDiscount"
                    checked={!!field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Label htmlFor="goodStudentDiscount">Good Student Discount?</Label>
            </div>
          </div>

          <div className="space-y-2 text-xs text-gray-500">
            <p>
              Distant Student Discount criteria: under 23, rated driver, not named insured or
              spouse, full-time student, no vehicle while away, and more than 100 miles from home.
            </p>
            <p>
              Good Student Discount criteria: rated driver, not named insured or spouse, full-time
              student under 23, GPA 3.0+ or top 20% of class.
            </p>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!formState.isValid}
            >
              Save Driver
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Drivers() {
  const { control, getValues } = useFormContext();
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: "step4.drivers",
  });
  const drivers = useWatch({ control, name: "step4.drivers" }) || [];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState(null);

  const openCreate = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEdit = (index) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (values) => {
    if (editingIndex === null) {
      append(values);
    } else {
      update(editingIndex, values);
    }
    setDialogOpen(false);
  };

  const insured = getValues("step2");
  const firstDriverDefaults =
    drivers.length === 0
      ? {
          ...emptyDriver,
          firstName: insured?.firstName || "",
          lastName: insured?.lastName || "",
          dateOfBirth: insured?.dateOfBirth || "",
          gender: insured?.gender || "",
        }
      : emptyDriver;

  const defaultValues =
    editingIndex === null
      ? firstDriverDefaults
      : drivers[editingIndex] || emptyDriver;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Drivers</h2>
        <Button
          type="button"
          className="bg-red-600 hover:bg-red-700 text-white"
          onClick={openCreate}
          disabled={drivers.length >= 6}
        >
          Add Driver
        </Button>
      </div>

      {drivers.length >= 6 && (
        <p className="text-sm text-gray-500">Maximum of 6 drivers reached.</p>
      )}

      <div className="space-y-3">
        {fields.length === 0 && (
          <p className="text-sm text-gray-500">No drivers added yet.</p>
        )}
        {fields.map((field, index) => {
          const driver = drivers[index] || field;
          const name = `${driver.firstName || "First"} ${driver.lastName || "Last"}`;
          return (
            <div
              key={field.id}
              className="flex items-center justify-between rounded-lg border border-slate-200 p-4"
            >
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Driver {index + 1}: {name}
                </div>
                <div className="text-xs text-slate-500">
                  DOB: {driver.dateOfBirth ? new Date(driver.dateOfBirth).toLocaleDateString() : "N/A"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => openEdit(index)}>
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => remove(index)}
                >
                  Remove
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <FieldError step="step4" name="step4.drivers" />

      <DriverDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        defaultValues={defaultValues}
      />
    </div>
  );
}
